// Formatting and the small statistics the views share.
//
// One place, so a figure is written the same way wherever it appears.

/** Money. A dash where there is no reading — never a zero. */
export function usd(v: number | null | undefined): string {
  if (!Number.isFinite(v)) return '—';
  const n = v as number;
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  // A decimal through the low thousands: without it an average deposit of
  // $1,450 and one of $1,940 both print as "$1K", and that is exactly the range
  // most per-player averages fall in.
  if (a >= 1e4) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

/** A number of things, so it is rounded — half a withdrawal does not exist. */
export const count = (v: number | null | undefined): string =>
  (Number.isFinite(v) ? Math.round(v as number).toLocaleString('en-GB') : '—');

export const pct = (v: number | null | undefined, d = 1): string =>
  (Number.isFinite(v) ? `${(v as number).toFixed(d)}%` : '—');

export const signedPct = (v: number | null | undefined, d = 1): string =>
  (Number.isFinite(v) ? `${(v as number) > 0 ? '+' : ''}${(v as number).toFixed(d)}%` : '—');

export function ago(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'at an unknown time';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/** The middle reading. Undefined values are dropped, not counted as zero. */
export function median(values: readonly (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => Number.isFinite(v));
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = s.length >> 1;
  // A `!` would be a lie here — noUncheckedIndexedAccess is on for a reason —
  // so the pair is read defensively even though the length guarantees them.
  if (s.length % 2) return s[mid] ?? null;
  const lo = s[mid - 1];
  const hi = s[mid];
  return lo !== undefined && hi !== undefined ? (lo + hi) / 2 : null;
}

/** How far the highest and lowest sit apart, against the middle reading. */
export function spreadOf(values: readonly (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => Number.isFinite(v));
  if (nums.length < 2) return null;
  const mid = median(nums);
  if (mid === null || mid === 0) return null;
  return ((Math.max(...nums) - Math.min(...nums)) / Math.abs(mid)) * 100;
}

/** Four steps of disagreement, quiet to loud. Used for colour, never for text. */
export type GapStep = 0 | 1 | 2 | 3;
export function gapStep(spreadPct: number | null | undefined): GapStep {
  if (!Number.isFinite(spreadPct)) return 0;
  const v = Math.abs(spreadPct as number);
  if (v < 5) return 0;
  if (v < 15) return 1;
  if (v < 40) return 2;
  return 3;
}

/**
 * One phrasing for the spread, everywhere.
 *
 * Short enough to sit on one line in a third-width column — a sub-line that
 * wraps gives every row in a column a different height. The long form is the
 * tooltip, and the glossary defines "apart" on every page.
 */
export function apartLabel(spreadPct: number | null | undefined, n: number): string {
  if (!Number.isFinite(spreadPct)) {
    return n === 1 ? 'one tracker only' : 'not comparable';
  }
  const v = spreadPct as number;
  if (v < 1) return `${n} trackers · they agree`;
  return `${n} trackers · ${v.toFixed(0)}% apart`;
}

export const apartTitle = (spreadPct: number | null | undefined, n: number): string =>
  (Number.isFinite(spreadPct)
    ? `${n} trackers publish this. The highest reading is ${(spreadPct as number).toFixed(0)}% `
      + 'above the lowest, measured against the middle reading.'
    : 'Only one tracker publishes this, so there is nothing to compare it against.');

/** Chain codes as the trackers publish them, mapped to the names people use. */
const CHAIN_NAMES: Record<string, string> = {
  ETH: 'Ethereum', TRX: 'Tron', SOL: 'Solana', BSC: 'BNB Chain',
  POLYGON: 'Polygon', POLY: 'Polygon', XRP: 'XRP Ledger', BTC: 'Bitcoin',
  BASE: 'Base', ARB: 'Arbitrum', TON: 'TON', AVAX: 'Avalanche',
  LTC: 'Litecoin', DOGE: 'Dogecoin', OP: 'Optimism', ADA: 'Cardano',
  BCH: 'Bitcoin Cash', HBAR: 'Hedera', XLM: 'Stellar', DASH: 'Dash',
};
export const chainName = (code: string): string => CHAIN_NAMES[code] ?? code;
